import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Download, Users, Mail, Phone, Building2, Calendar,
  Search, ChevronRight, Menu, X, LayoutDashboard, Briefcase,
  ClipboardList, Trophy, Crown, UserPlus, Star, BarChart3, CreditCard,
  LogOut
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { CompanyTeamsPanel } from '../components/events/CompanyTeamsPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const EventParticipantsPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('participants');

  const token = localStorage.getItem('company_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/company/auth'); return; }
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      const [eventRes, partRes] = await Promise.all([
        axios.get(`${API}/api/company/events/${eventId}`, { headers }),
        axios.get(`${API}/api/company/events/${eventId}/participants`, { headers }),
      ]);
      setEvent(eventRes.data);
      if (eventRes.data.registration_mode === 'team') setTab('teams');
      setParticipants(partRes.data.participants || []);
    } catch {
      toast.error('Failed to load data');
    } finally { setLoading(false); }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/api/company/events/${eventId}/participants/export`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `event_participants_${eventId}.csv`;
      a.click();
      toast.success('Downloaded CSV');
    } catch { toast.error('Export failed'); }
  };

  const filtered = participants.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f3f2ef]">
      <div className="w-8 h-8 border-2 border-[#0a66c2] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f3f2ef]" data-testid="event-participants-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-2 text-sm text-[#666] mb-3">
            <Link to="/company/events" className="hover:text-[#0a66c2] flex items-center gap-1"><ArrowLeft size={14} /> Events</Link>
            <ChevronRight size={14} />
            <span className="text-[#191919] font-medium">Participants</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-[#191919]">{event?.title || 'Event'}</h1>
              <p className="text-sm text-[#666] mt-0.5">{participants.length} registered participants</p>
            </div>
            <button onClick={handleExport} className="flex items-center gap-2 bg-[#0a66c2] hover:bg-[#004182] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-testid="export-csv-btn">
              <Download size={16} /> Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {event?.registration_mode === 'team' && (
          <div className="flex gap-1 mb-6 bg-white border border-gray-200 rounded-lg p-1 w-fit">
            {['teams', 'participants'].map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`participants-tab-${t}`}
                className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize ${tab === t ? 'bg-[#0a66c2] text-white' : 'text-[#666]'}`}>{t}</button>
            ))}
          </div>
        )}
        {tab === 'teams' ? <CompanyTeamsPanel eventId={eventId} headers={headers} /> : (<>
        {/* Search */}
        <div className="relative mb-6">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            className="w-full max-w-md pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#0a66c2]"
            placeholder="Search by name or email..." data-testid="participant-search" />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left font-semibold text-[#666] text-xs uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left font-semibold text-[#666] text-xs uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left font-semibold text-[#666] text-xs uppercase tracking-wider">Phone</th>
                  <th className="px-4 py-3 text-left font-semibold text-[#666] text-xs uppercase tracking-wider">College</th>
                  <th className="px-4 py-3 text-left font-semibold text-[#666] text-xs uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-[#666] text-xs uppercase tracking-wider">Payment</th>
                  <th className="px-4 py-3 text-left font-semibold text-[#666] text-xs uppercase tracking-wider">Registered</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-[#666]">No participants found</td></tr>
                ) : filtered.map((p, i) => (
                  <tr key={p.id || i} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`participant-row-${i}`}>
                    <td className="px-4 py-3 font-medium text-[#191919]">{p.name}</td>
                    <td className="px-4 py-3 text-[#666]">{p.email}</td>
                    <td className="px-4 py-3 text-[#666]">{p.phone || '—'}</td>
                    <td className="px-4 py-3 text-[#666]">{p.college || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.registration_type === 'codearena' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                        {p.registration_type === 'codearena' ? 'EvalentX Arena' : p.registration_type === 'team' ? `Team ${p.team_name || ''}` : 'Guest'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : p.payment_status === 'free' ? 'bg-gray-100 text-gray-600' : 'bg-amber-50 text-amber-700'}`}>
                        {p.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#666] text-xs">{p.registered_at ? new Date(p.registered_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>)}
      </div>
    </div>
  );
};

export default EventParticipantsPage;
